import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  History,
  Loader2,
  Search,
  Filter,
  Download,
  RefreshCw,
  Shield,
  UserPlus,
  UserMinus,
  Edit,
  Copy,
  ChevronLeft,
  ChevronRight,
  Calendar,
  User,
  Clock,
  FileText,
  AlertCircle,
  CheckCircle,
  XCircle,
  Activity
} from "lucide-react";

const ACTION_CONFIG = {
  create: { 
    label: "Creación", 
    icon: UserPlus, 
    color: "bg-green-100 text-green-700 border-green-200",
    iconColor: "text-green-600"
  },
  update: { 
    label: "Actualización", 
    icon: Edit, 
    color: "bg-blue-100 text-blue-700 border-blue-200",
    iconColor: "text-blue-600"
  },
  delete: { 
    label: "Eliminación", 
    icon: UserMinus, 
    color: "bg-red-100 text-red-700 border-red-200",
    iconColor: "text-red-600"
  },
  clone: { 
    label: "Clonación", 
    icon: Copy, 
    color: "bg-purple-100 text-purple-700 border-purple-200",
    iconColor: "text-purple-600"
  }
};

export default function AuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_action: {}, recent_7_days: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [actionFilter, setActionFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("");
  const [page, setPage] = useState(0);
  const [totalLogs, setTotalLogs] = useState(0);
  const [selectedLog, setSelectedLog] = useState(null);
  const pageSize = 20;
  const { getAuthHeaders, user } = useAuth();

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append("limit", pageSize);
      params.append("skip", page * pageSize);
      if (actionFilter !== "all") params.append("action", actionFilter);
      if (roleFilter) params.append("role_id", roleFilter);

      const [logsRes, statsRes] = await Promise.all([
        axios.get(`${API}/audit-logs?${params.toString()}`, getAuthHeaders()),
        axios.get(`${API}/audit-logs/stats`, getAuthHeaders())
      ]);

      setLogs(logsRes.data.logs || []);
      setTotalLogs(logsRes.data.total || 0);
      setStats(statsRes.data || { total: 0, by_action: {}, recent_7_days: 0 });
    } catch (err) {
      console.error("Error fetching audit logs:", err);
      toast.error("Error al cargar el log de auditoría");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, page, actionFilter, roleFilter]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    return date.toLocaleDateString("es", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return "Hace un momento";
    if (minutes < 60) return `Hace ${minutes} min`;
    if (hours < 24) return `Hace ${hours} hora${hours > 1 ? "s" : ""}`;
    if (days < 7) return `Hace ${days} día${days > 1 ? "s" : ""}`;
    return formatDate(timestamp);
  };

  const exportToCSV = () => {
    if (logs.length === 0) {
      toast.error("No hay datos para exportar");
      return;
    }

    const headers = ["Fecha", "Acción", "Rol", "Usuario", "Email", "Detalles"];
    const rows = logs.map(log => [
      formatDate(log.timestamp),
      ACTION_CONFIG[log.action]?.label || log.action,
      log.role_name,
      log.user_name,
      log.user_email,
      log.details || ""
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map(cell => `"${(cell || "").replace(/"/g, '""')}"`).join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `audit_log_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success("Archivo CSV descargado");
  };

  const filteredLogs = logs.filter(log => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      log.role_name?.toLowerCase().includes(search) ||
      log.user_name?.toLowerCase().includes(search) ||
      log.user_email?.toLowerCase().includes(search) ||
      log.details?.toLowerCase().includes(search)
    );
  });

  const totalPages = Math.ceil(totalLogs / pageSize);

  const isAdmin = user?.role?.toLowerCase() === "admin" || user?.role?.toLowerCase() === "administrador";

  if (!isAdmin) {
    return (
      <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
        <Sidebar />
        <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden">
          <div className="flex flex-col items-center justify-center h-full">
            <Shield className="w-16 h-16 text-slate-300 mb-4" />
            <h2 className="text-xl font-semibold text-slate-600">Acceso Restringido</h2>
            <p className="text-slate-500">Solo administradores pueden ver el log de auditoría</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="audit-log-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
              <History className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-['Manrope']">
                Log de Auditoría
              </h1>
              <p className="text-slate-500 dark:text-slate-400 mt-1">
                Historial de cambios en permisos y roles
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={fetchLogs}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualizar
            </Button>
            <Button variant="outline" size="sm" onClick={exportToCSV}>
              <Download className="w-4 h-4 mr-2" />
              Exportar CSV
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="border-slate-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total de Registros</p>
                  <p className="text-3xl font-bold text-slate-900 dark:text-white">{stats.total}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-slate-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-green-200 bg-green-50/50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600">Creaciones</p>
                  <p className="text-3xl font-bold text-green-700">{stats.by_action?.create || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                  <UserPlus className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-blue-200 bg-blue-50/50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600">Actualizaciones</p>
                  <p className="text-3xl font-bold text-blue-700">{stats.by_action?.update || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                  <Edit className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-amber-200 bg-amber-50/50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-amber-600">Últimos 7 días</p>
                  <p className="text-3xl font-bold text-amber-700">{stats.recent_7_days}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                  <Activity className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Buscar por rol, usuario o detalle..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <div className="w-48">
                <Select value={actionFilter} onValueChange={(v) => { setActionFilter(v); setPage(0); }}>
                  <SelectTrigger>
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Filtrar por acción" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas las acciones</SelectItem>
                    <SelectItem value="create">Creaciones</SelectItem>
                    <SelectItem value="update">Actualizaciones</SelectItem>
                    <SelectItem value="delete">Eliminaciones</SelectItem>
                    <SelectItem value="clone">Clonaciones</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Timeline */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : filteredLogs.length === 0 ? (
          <Card className="border-dashed border-2">
            <CardContent className="py-12 text-center">
              <History className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-semibold text-slate-600 mb-2">Sin registros de auditoría</h3>
              <p className="text-slate-500">
                {searchTerm || actionFilter !== "all" 
                  ? "No se encontraron registros con los filtros aplicados"
                  : "Los cambios en permisos y roles aparecerán aquí"
                }
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredLogs.map((log, index) => {
              const config = ACTION_CONFIG[log.action] || ACTION_CONFIG.update;
              const Icon = config.icon;
              
              return (
                <Card 
                  key={log.id || index} 
                  className={`hover:shadow-md transition-all cursor-pointer ${
                    selectedLog?.id === log.id ? "ring-2 ring-indigo-500" : ""
                  }`}
                  onClick={() => setSelectedLog(selectedLog?.id === log.id ? null : log)}
                  data-testid={`audit-log-${log.id}`}
                >
                  <CardContent className="py-4">
                    <div className="flex items-start gap-4">
                      {/* Icon */}
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${config.color}`}>
                        <Icon className={`w-5 h-5 ${config.iconColor}`} />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline" className={config.color}>
                            {config.label}
                          </Badge>
                          <span className="text-sm font-semibold text-slate-900 dark:text-white">
                            {log.role_name}
                          </span>
                        </div>
                        
                        <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">
                          {log.details || `${config.label} del rol "${log.role_name}"`}
                        </p>

                        <div className="flex items-center gap-4 text-xs text-slate-500">
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {log.user_name || log.user_email}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatRelativeTime(log.timestamp)}
                          </span>
                        </div>

                        {/* Expanded details */}
                        {selectedLog?.id === log.id && log.changes && (
                          <div className="mt-4 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                            <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                              <FileText className="w-4 h-4" />
                              Detalles del cambio
                            </h4>
                            
                            {log.changes.before && (
                              <div className="mb-3">
                                <p className="text-xs text-slate-500 mb-1">Permisos anteriores:</p>
                                <pre className="text-xs bg-red-50 dark:bg-red-900/20 p-2 rounded overflow-x-auto max-h-32">
                                  {JSON.stringify(log.changes.before, null, 2)}
                                </pre>
                              </div>
                            )}
                            
                            {log.changes.after && (
                              <div>
                                <p className="text-xs text-slate-500 mb-1">Permisos nuevos:</p>
                                <pre className="text-xs bg-green-50 dark:bg-green-900/20 p-2 rounded overflow-x-auto max-h-32">
                                  {JSON.stringify(log.changes.after, null, 2)}
                                </pre>
                              </div>
                            )}

                            <div className="mt-3 pt-3 border-t text-xs text-slate-500">
                              <p><strong>ID del registro:</strong> {log.id}</p>
                              <p><strong>Fecha exacta:</strong> {formatDate(log.timestamp)}</p>
                              <p><strong>Email:</strong> {log.user_email}</p>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Time */}
                      <div className="text-right text-xs text-slate-400">
                        <Calendar className="w-3 h-3 inline mr-1" />
                        {formatDate(log.timestamp).split(",")[0]}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between pt-4">
                <p className="text-sm text-slate-500">
                  Mostrando {page * pageSize + 1} - {Math.min((page + 1) * pageSize, totalLogs)} de {totalLogs} registros
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                    disabled={page === 0}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm text-slate-600">
                    Página {page + 1} de {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                    disabled={page >= totalPages - 1}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
